import { tableStore } from '../stores/table-store';
import { isGroupRow } from '../stores/utils/group-row-guards';
import {
  checkSolutionHealth,
  type HealthIssue,
} from '../stores/utils/solution-health';
import { isServiceScriptFile, transformActionFileData } from '../utils';
import { useProStudioCtxSelector } from '../xstate/app.xstate';

/**
 * The last check, and what it was run on. The Build bar's badge, the header's
 * Publish and the Health panel all ask on every render — and those render
 * often (scrolling the table re-renders the header) — so the check runs only
 * when its inputs change. `getAllSortedRows()` hands back the same array
 * until the rows change, so identity is enough to tell.
 */
let cache: {
  rows: unknown;
  commands: unknown;
  scripts: string;
  issues: HealthIssue[];
} | null = null;

// Shared by every disabled caller, so frozen: a caller that sorted or pushed
// into it in place would leak issues into every other one.
const NO_ISSUES = Object.freeze([]) as unknown as HealthIssue[];
const NO_SCRIPTS: string[] = [];

/**
 * The solution's health, from the rule-based checks in
 * `stores/utils/solution-health.ts`. Read from an observer so it re-runs as
 * rows change.
 *
 * `enabled: false` returns no issues without reading the rows: the primary
 * nav calls this on every solution page but uses the result only with the
 * five-stage nav on (PSTUDIO-4572). It is a parameter rather than a
 * conditional call so the caller's hook order does not change.
 */
export const useSolutionHealth = ({
  enabled = true,
}: { enabled?: boolean } = {}): HealthIssue[] => {
  const scripts = useProStudioCtxSelector(
    (ctx) =>
      enabled
        ? (ctx.draft?.files ?? [])
            .filter(isServiceScriptFile)
            .map((f) => transformActionFileData(f).name)
        : NO_SCRIPTS,
    { isEqualityDeep: true }
  );
  if (!enabled) return NO_ISSUES;
  const allRows = tableStore.getAllSortedRows();
  const commands = tableStore.actionNodeNames;
  const scriptsKey = scripts.join('\n');
  if (
    cache &&
    cache.rows === allRows &&
    cache.commands === commands &&
    cache.scripts === scriptsKey
  ) {
    return cache.issues;
  }
  const issues = checkSolutionHealth(
    allRows.filter((r) => !isGroupRow(r)),
    { nativeCommands: commands, uploadedScripts: scripts }
  );
  cache = { rows: allRows, commands, scripts: scriptsKey, issues };
  return issues;
};
